import React from 'react';
import { useFormContext } from 'react-hook-form';
import ChipInput from '../../NRControlledChipInput/NRControlledChipInput';
import { CONSTANTS } from '../../../constants/constants';
import { validateCPE } from '../../../utils/utils';

/**
 * CPE-only chip input for asset upload / edit forms.
 * Expects form fields: assetCpes (string[]), cpeInput, cpeInputError.
 */
export default function NRAssetCpeChipInput({
  name = 'cpeInput',
  listName = 'assetCpes',
  label = CONSTANTS.TRANSACTION_ASSET.ASSET_CPE_LABEL,
  placeholder = 'Enter CPE (cpe:...), then press Enter',
  helperText = 'Input then validate a CPE one at a time by pressing Enter, multiple entries are allowed.'
}) {
  const { control, setValue, watch } = useFormContext();
  const assetCpes = watch(listName) || [];
  const cpeInputError = watch(`${name}Error`);

  const handleAddCpe = identifier => {
    if (!identifier || !identifier.trim()) return false;

    const trimmed = identifier.trim();

    if (!trimmed.startsWith(CONSTANTS.CPE_PREFIX)) {
      setValue(`${name}Error`, `CPE must start with "${CONSTANTS.CPE_PREFIX}"`);
      return false;
    }

    const validationResult = validateCPE(trimmed);
    if (validationResult !== true) {
      setValue(`${name}Error`, validationResult);
      return false;
    }

    if (assetCpes.includes(trimmed)) {
      setValue(`${name}Error`, 'This CPE has already been added');
      return false;
    }

    setValue(listName, [...assetCpes, trimmed]);
    setValue(name, '');
    setValue(`${name}Error`, '');
    return true;
  };

  return (
    <ChipInput
      name={name}
      control={control}
      setValue={setValue}
      values={assetCpes}
      setValues={vals => setValue(listName, vals)}
      label={label}
      placeholder={placeholder}
      error={cpeInputError}
      helperText={helperText}
      helperErrorText={cpeInputError}
      onEnter={handleAddCpe}
      onRestore={chip => {
        if (chip?.startsWith(CONSTANTS.CPE_PREFIX)) {
          setValue(listName, [...assetCpes, chip]);
        }
      }}
    />
  );
}

/** Flush pending cpeInput into assetCpes before submit. Returns false if pending input is invalid. */
export function flushPendingAssetCpe({ getValues, setValue, name = 'cpeInput', listName = 'assetCpes' }) {
  const pending = getValues(name);
  if (!pending || !pending.trim()) return true;

  const trimmed = pending.trim();
  if (!trimmed.startsWith(CONSTANTS.CPE_PREFIX)) {
    setValue(`${name}Error`, `CPE must start with "${CONSTANTS.CPE_PREFIX}"`);
    return false;
  }

  const validationResult = validateCPE(trimmed);
  if (validationResult !== true) {
    setValue(`${name}Error`, validationResult);
    return false;
  }

  const assetCpes = getValues(listName) || [];
  if (!assetCpes.includes(trimmed)) {
    setValue(listName, [...assetCpes, trimmed]);
  }
  setValue(name, '');
  setValue(`${name}Error`, '');
  return true;
}
